/**
 * Sign Up Form Component - Account ID Version
 * Allows users to create accounts with tenant selection (SUBSCRIPTION model only)
 * Displays Account ID with fallback UI if email delivery fails
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { signUpUser, getAvailableTenants } from '../../services/auth/cognitoClient';

export default function SignUpForm({ onSuccess, onSignIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [handle, setHandle] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(true);

  // Account ID dialog state
  const [showAccountIdDialog, setShowAccountIdDialog] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch available tenants on mount
  useEffect(() => {
    async function fetchTenants() {
      try {
        const availableTenants = await getAvailableTenants();
        setTenants(availableTenants);

        // Auto-select if only one tenant
        if (availableTenants.length === 1) {
          setSelectedTenantId(availableTenants[0].tenantId);
        }
      } catch (err) {
        console.error('[SignUpForm] Failed to fetch tenants:', err);
        setError('Failed to load available organizations. Please try again.');
      } finally {
        setLoadingTenants(false);
      }
    }

    fetchTenants();
  }, []);

  // Password validation
  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Confirm password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const result = await signUpUser({
        email,
        password,
        handle,
        selectedTenantId
      });

      console.log('[SignUpForm] Signup successful:', result);

      // Show Account ID dialog
      setAccountId(result.accountId);
      setEmailSent(result.emailSent);
      setShowAccountIdDialog(true);

    } catch (err) {
      console.error('[SignUpForm] Signup error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
      setLoading(false);
    }
  }

  // Copy Account ID to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(accountId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Download Account ID as text file
  const handleDownload = () => {
    const blob = new Blob(
      [`Your Account ID: ${accountId}\n\nEmail: ${email}\nOrganization: ${tenants.find(t => t.tenantId === selectedTenantId)?.tenantName || selectedTenantId}\n\nKeep this information safe. You'll need your Account ID and email to sign in.`],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `account-id-${accountId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Format Account ID with spaces for display
  const formatAccountId = (id) => {
    return id.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3');
  };

  if (loadingTenants) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tenants.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Self-service signup is not currently available. Please contact your administrator.
        </Alert>
        <Button onClick={onSignIn} sx={{ mt: 2 }}>
          Back to Login
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Create Account
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth required>
          <InputLabel>Organization</InputLabel>
          <Select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            disabled={loading || tenants.length === 1}
            label="Organization"
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                {tenant.tenantName || tenant.tenantId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Display Name"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          disabled={loading}
          required
          helperText="Your name as it will appear in the system"
        />

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          required
          autoComplete="email"
        />

        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          required
          helperText="At least 8 characters with uppercase, lowercase, and number"
          autoComplete="new-password"
        />

        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
          required
          autoComplete="new-password"
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 1, bgcolor: '#2d3e50', '&:hover': { bgcolor: '#3d4e60' } }}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Account'}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button variant="text" onClick={onSignIn} disabled={loading}>
            Already have an account? Sign In
          </Button>
        </Box>
      </Box>

      {/* Account ID Display Dialog */}
      <Dialog
        open={showAccountIdDialog}
        onClose={() => {}} // Prevent closing
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon color="success" />
            Account Created Successfully!
          </Box>
        </DialogTitle>
        <DialogContent>
          {!emailSent && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
              <strong>Account ID not emailed.</strong> Please save your Account ID - you'll need it to sign in.
            </Alert>
          )}

          {emailSent && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your Account ID has been emailed to <strong>{email}</strong>
            </Alert>
          )}

          <Paper
            elevation={3}
            sx={{
              p: 3,
              bgcolor: emailSent ? '#e3f2fd' : '#fff3cd',
              textAlign: 'center',
              border: emailSent ? '2px solid #2196f3' : '2px solid #ffc107'
            }}
          >
            <Typography variant="body2" gutterBottom>
              Your Account ID:
            </Typography>
            <Typography
              variant="h3"
              component="div"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                letterSpacing: 3,
                color: emailSent ? '#1976d2' : '#f57c00',
                my: 2
              }}
            >
              {formatAccountId(accountId)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll need this Account ID and your email to sign in
            </Typography>
          </Paper>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCopy}
                startIcon={<ContentCopyIcon />}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </Tooltip>
            <Tooltip title="Download as text file">
              <Button
                variant="outlined"
                fullWidth
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
              >
                Download
              </Button>
            </Tooltip>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Important:</strong> Save this Account ID! You'll need it along with your email to sign in.
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I have saved my Account ID (copied, downloaded, or written down)
              </Typography>
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setShowAccountIdDialog(false);
              if (onSuccess) {
                onSuccess();
              } else {
                onSignIn();
              }
            }}
            disabled={!confirmed}
            fullWidth
          >
            Continue to Sign In
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
